import { useState, useCallback } from 'react';
import { tipoDocumentoApi } from '../api/tipoDocumentoApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de tipos de documento
 * Proporciona estado y funciones para operaciones CRUD de tipos de documento
 */
export const useTipoDocumentos = () => {
  const [tipoDocumentos, setTipoDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene todos los tipos de documento
   */
  const fetchTipoDocumentos = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoDocumentoApi.getAll();
      setTipoDocumentos(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de documento');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un tipo de documento específico por ID
   */
  const fetchTipoDocumentoById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoDocumentoApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el tipo de documento');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo tipo de documento
   */
  const createTipoDocumento = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoDocumentoApi.create(data);
      showSuccessToast('Tipo de documento creado exitosamente');
      
      // Refrescar la lista
      await fetchTipoDocumentos();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el tipo de documento');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoDocumentos]);

  /**
   * Actualiza un tipo de documento existente
   */
  const updateTipoDocumento = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoDocumentoApi.update(id, data);
      showSuccessToast('Tipo de documento actualizado exitosamente');
      
      // Refrescar la lista
      await fetchTipoDocumentos();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el tipo de documento');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoDocumentos]);

  /**
   * Elimina un tipo de documento
   */
  const deleteTipoDocumento = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await tipoDocumentoApi.delete(id);
      showSuccessToast('Tipo de documento eliminado exitosamente');
      
      // Refrescar la lista
      await fetchTipoDocumentos();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el tipo de documento');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoDocumentos]);

  // NO cargar automáticamente - solo cuando sea necesario
  // useEffect(() => {
  //   fetchTipoDocumentos();
  // }, [fetchTipoDocumentos]);

  return {
    // Estado
    tipoDocumentos,
    loading,
    error,
    
    // Funciones
    fetchTipoDocumentos,
    fetchTipoDocumentoById,
    createTipoDocumento,
    updateTipoDocumento,
    deleteTipoDocumento,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useTipoDocumentos;
